import React, { useState, useRef, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building, ArrowRight, Shield, Smartphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';

const Login: React.FC = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [pendingUserId, setPendingUserId] = useState<number | null>(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoAvailable, setSsoAvailable] = useState(false);

  React.useEffect(() => {
    // Check if user was blocked by IP whitelist
    const ipBlockedMessage = sessionStorage.getItem('ip_blocked_message');
    if (ipBlockedMessage) {
      toast.error(ipBlockedMessage, { duration: 6000 });
      sessionStorage.removeItem('ip_blocked_message');
    }

    // Check if SSO is available
    const checkSsoAvailability = async () => {
      try {
        const response = await api.get('/api/sso/config');
        setSsoAvailable(response.data.enabled || false);
      } catch (error) {
        setSsoAvailable(false);
      }
    };

    checkSsoAvailability();
  }, []);


  if (user) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (requiresMfa && pendingUserId) {
        // Handle MFA verification using stored userId
        const mfaResponse = await api.post('/api/mfa/verify', {
          userId: pendingUserId,
          token: formData.mfaCode
        });

        if (mfaResponse.data.success) {
          // Store tokens for backward compatibility (same as regular login)
          const { accessToken, refreshToken } = mfaResponse.data;
          if (accessToken && refreshToken) {
            tokenStorage.setTokens(accessToken, refreshToken);
            api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          }

          toast.success('Login successful');
          window.location.href = '/app/dashboard';
        }
      } else {
        // Regular login - First check for password change or MFA requirements
        try {
          const loginResponse = await api.post('/api/auth/login', {
            email: formData.email,
            password: formData.password
          });

          // Check if password change is required (admin-created accounts)
          if (loginResponse.data.requiresPasswordChange) {
            toast('Password change required', {
              icon: '🔒',
              duration: 4000,
            });
            navigate('/forced-password-change', {
              state: { tempToken: loginResponse.data.tempToken }
            });
            setLoading(false);
            return;
          }

          // Check if MFA SETUP is required (privileged accounts without MFA configured)
          if (loginResponse.data.requiresMfaSetup) {
            toast('MFA setup required for administrator accounts', {
              icon: '🔐',
              duration: 5000,
            });
            navigate('/mfa-setup', {
              state: {
                mfaSetupToken: loginResponse.data.mfaSetupToken,
                userId: loginResponse.data.userId,
                userEmail: loginResponse.data.userEmail,
                enforced: true
              }
            });
            setLoading(false);
            return;
          }

          // Check if MFA verification is required (user has MFA already enabled)
          if (loginResponse.data.requiresMfa && loginResponse.data.userId) {
            setPendingUserId(loginResponse.data.userId);
            setRequiresMfa(true);
            toast.success('Enter your 6-digit MFA code');
            setLoading(false);
            return;
          }

          // Normal login flow - use AuthContext
          const result = await login(formData.email, formData.password);
          if (!result) {
            setLoading(false);
          }
          // If result === true, the login was successful and the page will redirect
        } catch (error: any) {
          console.error('Login check error:', error);
          toast.error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
          setLoading(false);
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
      setLoading(false);
    }
  };

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    try {
      // First check if SSO is configured by making a request to the config endpoint
      const configResponse = await api.get('/api/sso/config');
      
      if (!configResponse.data.enabled) {
        toast.error('SSO is not configured on this server');
        setSsoLoading(false);
        return;
      }
      
      // Redirect to backend SSO login endpoint
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      window.location.href = `${baseUrl}/api/sso/login`;
    } catch (error: any) {
      console.error('SSO login error:', error);
      if (error.response?.status === 400) {
        toast.error('SSO is not properly configured');
      } else {
        toast.error('SSO login failed');
      }
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-super-dark-purple to-dusky-purple z-0"></div>

      {/* Content */}
      <div className="max-w-md w-full space-y-8 relative z-20">
        <div className="text-center">
          <div className="w-40 h-28 mx-auto mb-4 flex items-center justify-center">
            <img
              src="/images/jarvis_logo.png"
              alt="Jarvis Logo"
              className="h-28 w-40 object-contain"
              style={{ display: 'block' }}
            />
          </div>
          <h2 className="text-3xl font-bold text-white">JARVIS RCSA Platform</h2>
          <p className="mt-2 text-gray-300">AI-Powered Risk & Control Assessment</p>
        </div>

        <div className="bg-white bg-opacity-95 backdrop-blur-md rounded-xl shadow-2xl p-8 border border-white border-opacity-20">
          {/* SSO Login Option - Only show if available */}
          {ssoAvailable && (
            <>
              <div className="mb-6">
                <button
                  onClick={handleSsoLogin}
                  disabled={ssoLoading}
                  className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {ssoLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  ) : (
                    <>
                      <Building className="w-5 h-5 mr-3 text-gray-500" />
                      <span>Sign in with SSO</span>
                      <ArrowRight className="w-4 h-4 ml-2 text-gray-400" />
                    </>
                  )}
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-electric-blue focus:border-electric-blue transition-colors"
                placeholder="Enter your email address"
                required
                disabled={requiresMfa}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="block w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-electric-blue focus:border-electric-blue transition-colors"
                  placeholder="Enter your password"
                  required
                  disabled={requiresMfa}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  disabled={requiresMfa}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {requiresMfa && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Smartphone className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Two-Factor Authentication Required</span>
                </div>
                <div>
                  <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 mb-2">
                    6-Digit Authentication Code
                  </label>
                  <input
                    id="mfaCode"
                    type="text"
                    value={formData.mfaCode}
                    onChange={(e) => setFormData({ ...formData, mfaCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                  <p className="text-xs text-gray-600 mt-2">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (requiresMfa && formData.mfaCode.length !== 6)}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-electric-blue hover:bg-darker-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-electric-blue disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {requiresMfa ? 'Verifying...' : 'Signing in...'}
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  {requiresMfa ? 'Verify & Sign In' : 'Sign In'}
                </>
              )}
            </button>

            {!requiresMfa && (
              <div className="mt-3 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-dusky-purple hover:text-darker-blue transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            )}
          </form>

          {requiresMfa && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setRequiresMfa(false);
                  setFormData({ ...formData, mfaCode: '' });
                }}
                className="w-full text-sm text-gray-600 hover:text-gray-800 underline"
              >
                ← Back to login
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-dusky-purple hover:text-darker-blue transition-colors">
                Sign up
              </Link>
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Need a tenant for your organization?{' '}
              <Link to="/signup" className="font-medium text-dusky-purple hover:text-darker-blue transition-colors">
                Request Tenant
              </Link>
            </p>
          </div>

          {/* Security Features Notice */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <div className="flex items-center text-sm text-gray-600">
              <Shield className="w-4 h-4 mr-2 text-gray-500" />
              <span>Secure login with MFA and SSO support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;